import { useState } from 'react';
import { TicketHead } from '../../components/TicketHead';
import { versionLetter } from '../../lib/format';
import { PLATFORM_CHAR_LIMITS, countCharacters, platformLabel } from '../../lib/platforms';
import type {
  DraftVariantRead,
  GenerationRequestDetailRead,
  GenerationRequestRead,
  QuestionAnswer,
} from '../../types/api';
import {
  captionLengthLabel,
  captionLengthRange,
  formatLengthRange,
} from '../generation/captionOptions';
import { leadIntentLabel } from '../generation/intents';
import { useCaptionOptions } from '../generation/useCaptionOptions';
import { useIntents } from '../generation/useIntents';
import { CaptionEditor } from './CaptionEditor';
import { FactCheckPanel } from './FactCheckPanel';
import { FlagDialog } from './FlagDialog';
import { SendBackDialog } from './SendBackDialog';
import { useFlagVariant } from './useFlagVariant';
import type { RejectionFlow } from './useRejectRequest';
import { useSendBack } from './useSendBack';

/** Character share at which the count starts warning. */
const NEAR_LIMIT_RATIO = 0.9;

interface VariantCardProps {
  businessId: string;
  request: GenerationRequestDetailRead;
  variant: DraftVariantRead;
  isSubmitting: boolean;
  /** The screen's rejection flow: the cap and the navigation are its, not a card's. */
  rejection: RejectionFlow;
  onSelect: (variantId: string, questionAnswers: QuestionAnswer[]) => void;
  /** This variant, rewritten by hand: it replaces its own row on the request. */
  onCaptionSaved: (variant: DraftVariantRead) => void;
  /** The request, now `revising`: the page follows it back onto the stream. */
  onSentBack: (next: GenerationRequestRead) => void;
  /** The request moved on without us; reload it. */
  onStale: () => void;
}

function countClassName(count: number, limit: number): string {
  if (count > limit) return 'variant__count variant__count--over';
  if (count >= limit * NEAR_LIMIT_RATIO) return 'variant__count variant__count--near';
  return 'variant__count';
}

/**
 * One of the two caption variants in Review Step 1.
 *
 * The clarifying questions the fact-check agent attached to *this* variant must
 * all be answered before it can be chosen — that is the one hard gate on this
 * screen, and it comes from the user's own input, not from a verdict. The
 * verdicts themselves never block (SRS §3 stage 3).
 *
 * Answer state is local to the card and keyed on the variant id by the parent,
 * so a regenerated variant arrives with empty fields rather than stale answers.
 *
 * A brief that named two purposes gets a "Leads with" tag on each card: that
 * split is deliberate on the pipeline's side — variant 1 leads with the
 * primary, variant 2 with the secondary — and stating it is what makes the two
 * drafts worth comparing rather than looking arbitrary.
 *
 * Reporting the variant is available alongside choosing it and stays out of its
 * way: a flag goes to Autopostmate staff, changes nothing about the draft, and never
 * disables the button that picks it.
 *
 * The caption itself is editable in place ({@link CaptionEditor}), and editing
 * one card leaves the other exactly as it was — the editor owns its own draft
 * and only the saved variant reaches the page.
 *
 * Between those two there is **Send it back** ({@link SendBackDialog}): one
 * control for every complaint, which asks what kind of problem it is and turns
 * that into either a revision of this caption alone or a regeneration of the
 * pair. There is deliberately one of it per card — two buttons asking the same
 * question at two prices is what this replaced.
 */
export function VariantCard({
  businessId,
  request,
  variant,
  isSubmitting,
  rejection,
  onSelect,
  onCaptionSaved,
  onSentBack,
  onStale,
}: VariantCardProps) {
  const questions = variant.fact_check?.questions ?? [];
  const [answers, setAnswers] = useState<string[]>(() => questions.map(() => ''));
  const flag = useFlagVariant(businessId, variant.request_id, variant.id);
  const sendBack = useSendBack(businessId, request, variant, onSentBack, rejection);
  // Why the two drafts differ, when the brief named two purposes: variant 1
  // leads with the primary, variant 2 with the secondary. Null — and no tag —
  // for a brief with one purpose or none, where there is no split to explain.
  const { intents } = useIntents();
  const leadsWith = leadIntentLabel(request.intents, intents, variant.variant_index);

  const platform = request.platform;
  const count = countCharacters(variant.caption);
  const limit = PLATFORM_CHAR_LIMITS[platform];
  // The length the brief asked for, when it asked: the same codepoint count
  // the backend's length check uses. Outside the range is a note, never a gate.
  const { options: captionOptions } = useCaptionOptions();
  const lengthKey = request.caption_length;
  const target = lengthKey ? captionLengthRange(lengthKey, platform, captionOptions) : null;
  const isOffTarget = target !== null && (count < target.min || count > target.max);
  const isAnswered = questions.every((_, index) => (answers[index] ?? '').trim().length > 0);
  // Work in flight owns this card until the page follows the request onto the
  // stream: the words on screen are about to be replaced.
  const isBusy = isSubmitting || sendBack.isSubmitting;

  function handleAnswer(index: number, value: string) {
    setAnswers((current) =>
      current.map((answer, position) => (position === index ? value : answer)),
    );
  }

  function handleSelect() {
    const questionAnswers: QuestionAnswer[] = questions.map((question, index) => ({
      question,
      answer: (answers[index] ?? '').trim(),
    }));
    onSelect(variant.id, questionAnswers);
  }

  return (
    <article className="variant">
      <TicketHead
        label={`Version ${versionLetter(variant.variant_index)}`}
        timestamp={variant.created_at}
      />

      {leadsWith ? <p className="intent-tag variant__intent">Angle: {leadsWith}</p> : null}

      <CaptionEditor
        businessId={businessId}
        variant={variant}
        platform={platform}
        isDisabled={isBusy}
        hasFactCheckNearby={variant.fact_check !== null}
        onSaved={onCaptionSaved}
        onStale={onStale}
      />

      <p className="variant__meter">
        <span className={countClassName(count, limit)}>
          {count}/{limit}
        </span>
        <span className="variant__meter-label">characters on {platformLabel(platform)}</span>
        {lengthKey && target ? (
          <span
            className={isOffTarget ? 'variant__target variant__target--off' : 'variant__target'}
          >
            {captionLengthLabel(lengthKey, captionOptions)} target: {formatLengthRange(target)}
            {isOffTarget ? (count > target.max ? ', over' : ', under') : ''}
          </span>
        ) : null}
      </p>

      <FactCheckPanel factCheck={variant.fact_check} />

      {questions.length > 0 ? (
        <div className="variant__questions">
          <h3 className="variant__questions-title">Before you pick this one</h3>
          <p className="variant__questions-lede">
            We could not settle these on our own. Your answers are saved with your choice.
          </p>
          {questions.map((question, index) => (
            <label className="field" key={`${index}-${question}`}>
              <span className="field__label">{question}</span>
              <input
                className="field__input"
                type="text"
                value={answers[index] ?? ''}
                disabled={isBusy}
                onChange={(event) => handleAnswer(index, event.target.value)}
              />
            </label>
          ))}
        </div>
      ) : null}

      <div className="variant__actions">
        <button
          className="button button--primary"
          type="button"
          disabled={isBusy || !isAnswered}
          onClick={handleSelect}
        >
          Use this version
        </button>
        {/* The one way to say "not this" about this draft. Hidden while its own
            dialog is open — the dialog carries the action. */}
        {sendBack.isOpen ? null : (
          <button
            className="button button--ghost"
            type="button"
            disabled={isBusy}
            onClick={sendBack.open}
          >
            {sendBack.isSubmitting ? 'Sending' : 'Send it back'}
          </button>
        )}
        {/* Never disabled by `isSubmitting`: reporting is not a decision about
            this request, and it competes with nothing on this card. */}
        {flag.isFlagged ? (
          <p className="flag-note" role="status">
            Flagged for review
          </p>
        ) : (
          <button className="button button--ghost flag-action" type="button" onClick={flag.open}>
            Flag
          </button>
        )}
        {!isAnswered ? (
          <p className="variant__blocked">Answer every question above to choose this version.</p>
        ) : null}
      </div>

      {sendBack.isOpen ? (
        <SendBackDialog
          step="copy"
          regenerationsUsed={sendBack.regenerationsUsed}
          revisionsUsed={sendBack.revisionsUsed}
          isRegenerationCapped={sendBack.isRegenerationCapped}
          isRevisionCapped={sendBack.isRevisionCapped}
          isSubmitting={sendBack.isSubmitting}
          error={sendBack.error}
          onReasonChange={sendBack.clearError}
          onSubmit={sendBack.submit}
          onCancel={sendBack.close}
        />
      ) : null}

      {flag.isOpen ? (
        <FlagDialog
          subject={`version ${versionLetter(variant.variant_index)}`}
          isSubmitting={flag.isSubmitting}
          error={flag.error}
          onSubmit={flag.submit}
          onDismiss={flag.close}
        />
      ) : null}
    </article>
  );
}
